import React, { useMemo } from 'react'
import { Box, ButtonBase } from '@mui/material'

import { AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import { gridSx } from 'Pages/BusinessLogistics/Dashboards/OfficeNetwork/Page/@components/NetworkGrid'
import ChangeCell from './ChangeCell'
import { columnLabel } from '../@services/windowLabel'


const sx = {
	...gridSx,
	table: {
		...gridSx.table,
		'& th.rowh': { ...gridSx.table['& th.rowh'], minWidth: 150 },
		'& td.rowh': { ...gridSx.table['& td.rowh'], minWidth: 150, fontSize: 12 },
		'& th.selected': { boxShadow: `inset 0 -3px 0 ${dsColorNew.cyan}` },
		'& td.selected': { backgroundColor: '#F3F8FE' },
		'& tr.market td': { borderBottom: 0, pt: '4px' },
		'& tr.engine td': { pb: '4px' },
	},
	headBtn: { color: 'inherit', font: 'inherit', fontWeight: 700, borderRadius: '6px', px: 0.75, py: 0.25, display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
		'&:hover': { backgroundColor: 'rgba(255,255,255,.12)' }, '&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.cyan}` } },
	kind: { fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: dsColorNew.textSubtle },
}

/**
 * The heat map (plan § 4.3): a sticky ACRISS column and a sticky date header (Today / Tomorrow / the date, weekday
 * under), two sub-rows per ACRISS (Market · Engine), horizontal scroll. A date header is a button: it selects the
 * pick-up for every ACRISS (the pushes table below). A cell is a button: it opens the cell detail and selects it.
 */
export default function ChangeGrid({ data, sel, todayIso, onSelectPickup, onOpenCell }) {
	const dates = data?.sample?.dates || []
	const acriss = data?.sample?.acriss || []
	const byKey = useMemo(() => {
		const m = new Map()
		for (const c of data?.cells || []) m.set(`${c.sipp}|${c.pickup_date}`, c)
		return m
	}, [data])
	if (!acriss.length) return <AppEmptyLine>No ACRISS with a decision in this window.</AppEmptyLine>

	return <Box sx={sx.wrap}>
		<Box component="table" sx={sx.table}>
			<thead><tr>
				<th className="rowh">ACRISS</th>
				{dates.map(d => {
					const l = columnLabel(d, todayIso)
					const on = sel?.pickup === d && !sel?.sipp
					return <th key={d} className={on ? 'selected' : undefined} aria-sort="none">
						<ButtonBase onClick={() => onSelectPickup(d)} aria-pressed={on} aria-label={`Select every ACRISS of ${l.sub}`} title="Every ACRISS of this pick-up, below" sx={sx.headBtn}>
							<span>{l.top}</span><small>{l.sub}</small>
						</ButtonBase>
					</th>
				})}
			</tr></thead>
			<tbody>
				{acriss.map(code => <React.Fragment key={code}>
					<tr className="market">
						<td className="rowh" rowSpan={2}>
							<Box sx={{ fontWeight: 700, color: dsColorNew.textTitle, fontSize: 13 }}>{code}</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', mt: 0.5 }}><Box sx={sx.kind}>Market</Box><Box sx={sx.kind}>Engine</Box></Box>
						</td>
						{dates.map(d => {
							const cell = byKey.get(`${code}|${d}`)
							const on = sel?.pickup === d && (!sel?.sipp || sel.sipp === code)
							return <td key={d} className={on ? 'selected' : undefined}>
								<ChangeCell cell={cell} kind="market" aria={`${code} ${columnLabel(d, todayIso).sub}`} pressed={sel?.pickup === d && sel?.sipp === code} onOpen={() => onOpenCell(code, d)}/>
							</td>
						})}
					</tr>
					<tr className="engine">
						{dates.map(d => {
							const cell = byKey.get(`${code}|${d}`)
							const on = sel?.pickup === d && (!sel?.sipp || sel.sipp === code)
							return <td key={d} className={on ? 'selected' : undefined}>
								<ChangeCell cell={cell} kind="engine" aria={`${code} ${columnLabel(d, todayIso).sub}`} pressed={sel?.pickup === d && sel?.sipp === code} onOpen={() => onOpenCell(code, d)}/>
							</td>
						})}
					</tr>
				</React.Fragment>)}
			</tbody>
		</Box>
	</Box>
}
